import React from 'react';
import { View, Text, TouchableOpacity, Image, StyleSheet } from 'react-native';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { MARKETPLACE_COLORS } from '@/constants/residents/marketplaceColors';
import { getConditionDisplayText, ListingCondition } from '@/types/residents/marketplaceListing';

const PropertyPlaceholder = RESIDENTS_SVG_COMPONENTS.PROPERTY_PLACEHOLDER;

interface ListingRowCardProps {
  imageUri?: string;
  price: string;
  title: string;
  condition: ListingCondition;
  onPress: () => void;
  isSold?: boolean;
  rightElement?: React.ReactNode;
}

/**
 * Base card component for horizontal listing display (My Listings, Saved Listings)
 */
export const ListingRowCard: React.FC<ListingRowCardProps> = ({
  imageUri,
  price,
  title,
  condition,
  onPress,
  isSold = false,
  rightElement,
}) => (
  <TouchableOpacity 
    style={styles.container}
    onPress={onPress}
    activeOpacity={0.8}
  >
    {/* Thumbnail Image */}
    <View style={styles.imageContainer}>
      {imageUri ? (
        <Image
          source={{ uri: imageUri }}
          style={styles.image}
          resizeMode="cover"
        />
      ) : (
        <View style={styles.placeholderContainer}>
          <PropertyPlaceholder width={65} height={65} />
        </View>
      )}
    </View>
    
    {/* Content */}
    <View style={styles.content}>
      {/* Price Row */}
      <View style={styles.priceRow}>
        <View style={styles.priceContainer}>
          <Text style={styles.price}>{price}</Text>
          {isSold && (
            <>
              <Text style={styles.priceDot}> • </Text>
              <Text style={styles.soldText}>Sold</Text>
            </>
          )}
        </View>
        {rightElement}
      </View>
      
      {/* Title */}
      <Text style={styles.title} numberOfLines={2}>
        {title}
      </Text>
      
      {/* Condition */}
      <Text style={styles.condition}>
        {getConditionDisplayText(condition)}
      </Text>
    </View>
  </TouchableOpacity>
);

const styles = StyleSheet.create({
  container: {
    backgroundColor: MARKETPLACE_COLORS.cardBackground,
    borderRadius: 16,
    padding: 16,
    flexDirection: 'row',
    gap: 16,
  },
  imageContainer: {
    width: 85,
    height: 85,
    borderRadius: 14,
    borderWidth: 0.45,
    borderColor: MARKETPLACE_COLORS.borderGray,
    overflow: 'hidden',
  },
  image: {
    width: '100%',
    height: '100%',
  },
  placeholderContainer: {
    width: '100%',
    height: '100%',
    backgroundColor: MARKETPLACE_COLORS.uploadAreaBackground,
    alignItems: 'center',
    justifyContent: 'center',
  },
  content: {
    flex: 1,
    gap: 2,
  },
  priceRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  priceContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  price: {
    fontSize: 14,
    fontWeight: '600',
    color: MARKETPLACE_COLORS.textPrimary,
    lineHeight: 21,
  },
  priceDot: {
    fontSize: 14,
    fontWeight: '600',
    color: MARKETPLACE_COLORS.textPrimary,
    lineHeight: 21,
  },
  soldText: {
    fontSize: 14,
    fontWeight: '500',
    color: MARKETPLACE_COLORS.dangerRed,
    lineHeight: 21,
  },
  title: {
    fontSize: 14,
    fontWeight: '400',
    color: MARKETPLACE_COLORS.textPrimary,
    lineHeight: 21,
  },
  condition: {
    fontSize: 12,
    fontWeight: '400',
    color: MARKETPLACE_COLORS.textSecondary,
    lineHeight: 18,
  },
});

